import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Table, Form, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const ModalAdministrarPlanes = ({ show, onHide, onSuccess }) => {
    const { token } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();
    
    const [planes, setPlanes] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [planEditando, setPlanEditando] = useState(null);
    
    // Estados para modal de confirmación de eliminación
    const [showConfirmarEliminar, setShowConfirmarEliminar] = useState(false);
    const [planAEliminar, setPlanAEliminar] = useState(null);
    const [confirmacionTexto, setConfirmacionTexto] = useState('');
    const [eliminando, setEliminando] = useState(false);
    
    const [formData, setFormData] = useState({
        idRepublicaDominicanaPlanesFacturacion: 0,
        vPlanTitle: '',
        dPlanPrice: '',
        vPlanDesc: '',
        dCantidad: '',
        dDias: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            ObtenerPlanes();
        }
    }, [show]);

    const ObtenerPlanes = async () => {
        setCargando(true);
        
        try {
            const response = await fetch('/api/FacturaRepublicaDominicana/ObtenerCatalogoPlanes', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                if (data.iResponseType === 1) {
                    const planesData = JSON.parse(data.detalles);
                    setPlanes(planesData);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener planes');
                }
            } else {
                showErrorToast('Error al cargar planes');
            }
        } catch (error) {
            console.error('Error:', error);
            showErrorToast('Error al cargar planes');
        } finally {
            setCargando(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.vPlanTitle.trim()) {
            newErrors.vPlanTitle = 'El título del plan es requerido';
        } else if (formData.vPlanTitle.length > 500) {
            newErrors.vPlanTitle = 'El título no debe exceder 500 caracteres';
        }

        if (!formData.dPlanPrice) {
            newErrors.dPlanPrice = 'El precio es requerido';
        } else if (parseFloat(formData.dPlanPrice) < 0) {
            newErrors.dPlanPrice = 'El precio debe ser mayor o igual a 0';
        }

        if (!formData.vPlanDesc.trim()) {
            newErrors.vPlanDesc = 'La descripción es requerida';
        } else if (formData.vPlanDesc.length > 1000) {
            newErrors.vPlanDesc = 'La descripción no debe exceder 1000 caracteres';
        }

        if (!formData.dCantidad) {
            newErrors.dCantidad = 'La cantidad es requerida';
        } else if (parseInt(formData.dCantidad) < -1) {
            newErrors.dCantidad = 'La cantidad debe ser mayor o igual a -1 (ilimitado)';
        }

        if (!formData.dDias) {
            newErrors.dDias = 'Los días son requeridos';
        } else if (parseInt(formData.dDias) < 1) {
            newErrors.dDias = 'Los días deben ser mayor a 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setGuardando(true);
        
        const planData = {
            ...formData,
            idRepublicaDominicanaPlanesFacturacion: planEditando?.idRepublicaDominicanaPlanesFacturacion || 0,
            dPlanPrice: parseFloat(formData.dPlanPrice),
            dCantidad: parseInt(formData.dCantidad),
            dDias: parseInt(formData.dDias),
            bIsActive: true
        };

        try {
            const response = await fetch('/api/FacturaRepublicaDominicana/GuardarPlan/?jsonPlan=' + encodeURIComponent(JSON.stringify(planData)), {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                if (data.iResponseType === 1) {
                    // Usar el mensaje del servidor si está disponible, si no usar el genérico
                    const mensajeExito = data.vMessage || (planEditando ? 'Plan actualizado exitosamente' : 'Plan creado exitosamente');
                    showSuccessToast(mensajeExito);
                    handleCancelar();
                    ObtenerPlanes();
                    if (onSuccess) onSuccess();
                } else {
                    showErrorToast(data.vMessage || 'Error al guardar plan');
                }
            } else {
                showErrorToast('Error al guardar plan');
            }
        } catch (error) {
            console.error('Error:', error);
            showErrorToast('Error al guardar plan');
        } finally {
            setGuardando(false);
        }
    };

    const handleEditar = (plan) => {
        setModoEdicion(true);
        setPlanEditando(plan);
        setFormData({
            idRepublicaDominicanaPlanesFacturacion: plan.idRepublicaDominicanaPlanesFacturacion,
            vPlanTitle: plan.vPlanTitle || '',
            dPlanPrice: plan.dPlanPrice?.toString() || '',
            vPlanDesc: plan.vPlanDesc || '',
            dCantidad: plan.dCantidad?.toString() || '',
            dDias: plan.dDias?.toString() || ''
        });
    };

    const handleEliminar = async (idPlan) => {
        const plan = planes.find(p => p.idRepublicaDominicanaPlanesFacturacion === idPlan);
        setPlanAEliminar(plan);
        setShowConfirmarEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (confirmacionTexto !== 'ELIMINAR') {
            showErrorToast('Debe escribir "ELIMINAR" para confirmar');
            return;
        }

        setEliminando(true);

        try {
            const response = await fetch(`/api/FacturaRepublicaDominicana/EliminarPlan/?idPlan=${planAEliminar.idRepublicaDominicanaPlanesFacturacion}`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                if (data.iResponseType === 1) {
                    // Usar el mensaje del servidor si está disponible, si no usar el genérico
                    const mensajeExito = data.vMessage || 'Plan eliminado exitosamente';
                    showSuccessToast(mensajeExito);
                    cerrarModalConfirmacion();
                    ObtenerPlanes();
                    if (onSuccess) onSuccess();
                } else {
                    showErrorToast(data.vMessage || 'Error al eliminar plan');
                }
            } else {
                showErrorToast('Error al eliminar plan');
            }
        } catch (error) {
            console.error('Error:', error);
            showErrorToast('Error al eliminar plan');
        } finally {
            setEliminando(false);
        }
    };

    const cerrarModalConfirmacion = () => {
        setShowConfirmarEliminar(false);
        setPlanAEliminar(null);
        setConfirmacionTexto('');
    };

    const handleCancelar = () => {
        setModoEdicion(false);
        setPlanEditando(null);
        setFormData({
            idRepublicaDominicanaPlanesFacturacion: 0,
            vPlanTitle: '',
            dPlanPrice: '',
            vPlanDesc: '',
            dCantidad: '',
            dDias: ''
        });
        setErrors({});
    };

    const handleClose = () => {
        handleCancelar();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon="cog" className="me-2" />
                    Administrar Planes de Facturación
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={modoEdicion ? 8 : 12}>
                        {cargando ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3 text-muted">Cargando planes...</p>
                            </div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">
                                        <FontAwesomeIcon icon="list" className="me-2 text-primary" />
                                        Planes Disponibles
                                    </h6>
                                    {!modoEdicion && (
                                        <Button 
                                            variant="success" 
                                            size="sm"
                                            onClick={() => setModoEdicion(true)}
                                        >
                                            <FontAwesomeIcon icon="plus" className="me-2" />
                                            Nuevo Plan
                                        </Button>
                                    )}
                                </div>
                                
                                <div className="table-responsive">
                                    <Table striped bordered hover size="sm">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Plan</th>
                                                <th className="text-end">Precio</th>
                                                <th className="text-center">Folios</th>
                                                <th className="text-center">Días</th>
                                                <th className="text-center">Estado</th>
                                                <th className="text-center">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {planes.length > 0 ? (
                                                planes.map((plan) => (
                                                    <tr key={plan.idRepublicaDominicanaPlanesFacturacion}>
                                                        <td>
                                                            <div className="fw-semibold">{plan.vPlanTitle}</div>
                                                            <small className="text-muted">{plan.vPlanDesc}</small>
                                                        </td>
                                                        <td className="text-end fw-bold text-success">
                                                            ${parseFloat(plan.dPlanPrice).toFixed(2)}
                                                        </td>
                                                        <td className="text-center">
                                                            {plan.dCantidad === -1 ? (
                                                                <Badge bg="info">Ilimitado</Badge>
                                                            ) : (
                                                                plan.dCantidad.toLocaleString()
                                                            )}
                                                        </td>
                                                        <td className="text-center">{plan.dDias}</td>
                                                        <td className="text-center">
                                                            <Badge bg={plan.bIsActive ? 'success' : 'danger'}>
                                                                {plan.bIsActive ? 'Activo' : 'Inactivo'}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="btn-group btn-group-sm">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleEditar(plan)}
                                                                    title="Editar"
                                                                >
                                                                    <FontAwesomeIcon icon="edit" />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleEliminar(plan.idRepublicaDominicanaPlanesFacturacion)}
                                                                    title="Eliminar"
                                                                >
                                                                    <FontAwesomeIcon icon="trash" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        <FontAwesomeIcon icon="inbox" size="2x" className="text-muted mb-2" />
                                                        <p className="text-muted mb-0">No hay planes registrados</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </Col>
                    
                    {modoEdicion && (
                        <Col md={4}>
                            <div className="border-start ps-3">
                                <h6 className="mb-3">
                                    <FontAwesomeIcon icon={planEditando ? "edit" : "plus"} className="me-2 text-primary" />
                                    {planEditando ? 'Editar Plan' : 'Nuevo Plan'}
                                </h6>
                                
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Título del Plan *</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            name="vPlanTitle"
                                            value={formData.vPlanTitle}
                                            onChange={handleChange}
                                            isInvalid={!!errors.vPlanTitle}
                                            placeholder="Ej: Plan Básico Mensual"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.vPlanTitle}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Precio (USD) *</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            step="0.01"
                                            name="dPlanPrice"
                                            value={formData.dPlanPrice}
                                            onChange={handleChange}
                                            isInvalid={!!errors.dPlanPrice}
                                            placeholder="0.00"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.dPlanPrice}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Descripción *</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            size="sm"
                                            name="vPlanDesc"
                                            value={formData.vPlanDesc}
                                            onChange={handleChange}
                                            isInvalid={!!errors.vPlanDesc}
                                            placeholder="Descripción del plan"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.vPlanDesc}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Cantidad de Folios *</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            name="dCantidad"
                                            value={formData.dCantidad}
                                            onChange={handleChange}
                                            isInvalid={!!errors.dCantidad}
                                            placeholder="Ej: 100 (-1 para ilimitado)"
                                        />
                                        <Form.Text className="text-muted">
                                            <small>Use -1 para folios ilimitados</small>
                                        </Form.Text>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.dCantidad}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Días de Vigencia *</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="number"
                                            name="dDias"
                                            value={formData.dDias}
                                            onChange={handleChange}
                                            isInvalid={!!errors.dDias}
                                            placeholder="Ej: 30"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.dDias}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <div className="d-grid gap-2">
                                        <Button
                                            type="submit"
                                            variant="success"
                                            size="sm"
                                            disabled={guardando}
                                        >
                                            {guardando ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Guardando...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon="save" className="me-2" />
                                                    {planEditando ? 'Actualizar' : 'Guardar'}
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleCancelar}
                                            disabled={guardando}
                                        >
                                            <FontAwesomeIcon icon="times" className="me-2" />
                                            Cancelar
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cerrar
                </Button>
            </Modal.Footer>
            
            {/* Modal de Confirmación de Eliminación */}
            <Modal 
                show={showConfirmarEliminar} 
                onHide={cerrarModalConfirmacion} 
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                        Confirmar Eliminación
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {planAEliminar && (
                        <>
                            <div className="alert alert-warning">
                                <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                                <strong>¡Advertencia!</strong> Esta acción no se puede deshacer.
                            </div>
                            
                            <p className="mb-3">
                                Está a punto de eliminar el siguiente plan:
                            </p>
                            
                            <div className="card bg-light mb-3">
                                <div className="card-body">
                                    <h6 className="card-title mb-2">{planAEliminar.vPlanTitle}</h6>
                                    <p className="card-text text-muted small mb-1">{planAEliminar.vPlanDesc}</p>
                                    <div className="d-flex justify-content-between mt-2">
                                        <span className="badge bg-success">${parseFloat(planAEliminar.dPlanPrice).toFixed(2)}</span>
                                        <span className="badge bg-info">
                                            {planAEliminar.dCantidad === -1 ? 'Ilimitado' : `${planAEliminar.dCantidad} folios`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <p className="mb-2">
                                Para confirmar la eliminación, escriba <strong className="text-danger">ELIMINAR</strong> en el campo de abajo:
                            </p>
                            
                            <Form.Group>
                                <Form.Control
                                    type="text"
                                    value={confirmacionTexto}
                                    onChange={(e) => setConfirmacionTexto(e.target.value)}
                                    placeholder="Escriba ELIMINAR"
                                    autoFocus
                                    className={confirmacionTexto === 'ELIMINAR' ? 'border-success' : ''}
                                />
                                <Form.Text className="text-muted">
                                    Escriba exactamente "ELIMINAR" (en mayúsculas)
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={cerrarModalConfirmacion}
                        disabled={eliminando}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="danger"
                        onClick={confirmarEliminar}
                        disabled={confirmacionTexto !== 'ELIMINAR' || eliminando}
                    >
                        {eliminando ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Eliminando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="trash" className="me-2" />
                                Eliminar Plan
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

ModalAdministrarPlanes.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default ModalAdministrarPlanes;
